import { useState } from 'react';
import { PositionAnalysis, DEFAULT_SELL_TIERS } from '@/types/portfolio';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ChevronRight, TrendingUp, TrendingDown, ArrowUpDown } from 'lucide-react';

interface StockTableProps {
  analyses: PositionAnalysis[];
  onRowClick: (id: string) => void;
  sortBy?: string;
}

type TableSortOption = 'alertLevel' | 'ticker' | 'gainLoss' | 'totalValue' | 'sharesToSell' | 'valueToRecover' | 'recoveryPct';

export function StockTable({ analyses, onRowClick }: StockTableProps) {
  const [tableSortBy, setTableSortBy] = useState<TableSortOption>('alertLevel');

  // Debug: Log analyses data
  console.log('StockTable analyses:', analyses.map(a => ({ ticker: a.ticker, gainPct: a.gainPct })));

  if (analyses.length === 0) {
    return null;
  }

  const getAlertInfo = (gainPct: number) => {
    if (gainPct >= 45) return {
      color: 'bg-red-500',
      label: '🎯 100%',
      hasAlert: true
    };
    if (gainPct >= 30) return {
      color: 'bg-orange-500',
      label: '⚡ 40%',
      hasAlert: true
    };
    if (gainPct >= 15) return {
      color: 'bg-yellow-500',
      label: '💰 20%',
      hasAlert: true
    };
    return {
      color: 'bg-gray-400',
      label: 'Hold',
      hasAlert: false
    };
  };

  const getCurrencySymbol = (currency?: string): string => {
    if (!currency) return '$';
    switch (currency.toUpperCase()) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'CHF': return 'CHF';
      case 'GBP': return '£';
      case 'CAD': return 'CA$';
      case 'JPY': return '¥';
      default: return currency;
    }
  };

  // Sort the analyses based on selected option
  const sortedAnalyses = [...analyses].sort((a, b) => {
    switch (tableSortBy) {
      case 'alertLevel':
        // Sort by capital recovered percentage (highest first)
        return (b.capitalRecoveredPct || 0) - (a.capitalRecoveredPct || 0);
      case 'ticker':
        return a.ticker.localeCompare(b.ticker);
      case 'gainLoss':
        return (b.gainPct || 0) - (a.gainPct || 0);
      case 'totalValue':
        return (b.totalValueCHF || 0) - (a.totalValueCHF || 0);
      case 'sharesToSell':
        return (b.recommendedSellQty || 0) - (a.recommendedSellQty || 0);
      case 'valueToRecover':
        return (b.capitalRecovered || 0) - (a.capitalRecovered || 0);
      case 'recoveryPct':
        return (b.capitalRecoveredPct || 0) - (a.capitalRecoveredPct || 0);
      default:
        return 0;
    }
  });

  return (
    <Card className="mb-6">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <CardTitle>All Positions Overview</CardTitle>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Sort:</span>
              <Select value={tableSortBy} onValueChange={(value) => setTableSortBy(value as TableSortOption)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alertLevel">Alert Level</SelectItem>
                  <SelectItem value="ticker">Ticker</SelectItem>
                  <SelectItem value="gainLoss">Gain/Loss</SelectItem>
                  <SelectItem value="totalValue">Total Value</SelectItem>
                  <SelectItem value="sharesToSell">Shares to Sell</SelectItem>
                  <SelectItem value="valueToRecover">Value to Recover</SelectItem>
                  <SelectItem value="recoveryPct">Recovery %</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              {analyses.length} position{analyses.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2 sm:p-6">
        {/* Top horizontal scrollbar */}
        <div 
          className="overflow-x-auto -mx-2 sm:mx-0 mb-2"
          onScroll={(e) => {
            const mainScroll = e.currentTarget.parentElement?.querySelector('.main-scroll');
            if (mainScroll) {
              mainScroll.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <div style={{ width: '1800px', height: '1px' }}></div>
        </div>
        
        {/* Main table with both horizontal and vertical scroll */}
        <div 
          className="overflow-auto -mx-2 sm:mx-0 main-scroll max-h-[600px]"
          onScroll={(e) => {
            const topScroll = e.currentTarget.parentElement?.querySelector('.overflow-x-auto');
            if (topScroll) {
              topScroll.scrollLeft = e.currentTarget.scrollLeft;
            }
          }}
        >
          <table className="w-full min-w-[1800px]">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="sticky left-0 z-10 bg-background text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap border-r-2 border-border">Ticker</th>
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Stock Code</th>
                <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Market</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Last Price</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Bid</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Ask</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Gain/Loss</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Total Value (CHF)</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Next Target</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Stop Loss (5%)</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Shares to Sell</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Value to Recover</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Recovery %</th>
                <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-semibold text-xs sm:text-sm text-muted-foreground whitespace-nowrap">Alert</th>
                <th className="w-6 sm:w-8"></th>
              </tr>
            </thead>
            <tbody>
              {sortedAnalyses.map((position) => {
                const gainPctDisplay = (position.gainPct ?? 0) * 100;
                const alertInfo = getAlertInfo(gainPctDisplay);
                const currencySymbol = getCurrencySymbol(position.localCurrency);
                const isGain = position.gainPct >= 0;
                const fullRecoveryTier = DEFAULT_SELL_TIERS.find(tier => tier.cumulativeRecoverPct === 100);
                const fullRecoveryThreshold = fullRecoveryTier?.gainPct || 45;
                
                // Calculate dynamic recovery percentage matching the scorecard
                const recoveryPercentage = position.initialInvestment 
                  ? ((position.recommendedSellQty * position.currentPrice / position.initialInvestment) * 100).toFixed(2)
                  : 0;
                
                return (
                  <tr
                    key={position.id}
                    onClick={() => onRowClick(position.id)}
                    className={cn(
                      "border-b border-border/50 cursor-pointer transition-all hover:bg-muted/50",
                      "group",
                      alertInfo.hasAlert && "bg-muted/20"
                    )}
                  >
                    <td className="sticky left-0 z-10 bg-background py-2 sm:py-4 px-2 sm:px-4 border-r-2 border-border group-hover:bg-muted/50">
                      <span className="font-bold text-foreground text-sm sm:text-base whitespace-nowrap">{position.ticker}</span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4">
                      <span className="font-semibold text-primary text-xs sm:text-sm whitespace-nowrap">
                        {position.stockCode || '-'}
                      </span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4">
                      <span className="font-medium text-muted-foreground text-xs sm:text-sm whitespace-nowrap">
                        {position.localCurrency || 'USD'}
                      </span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      <span className="font-medium text-foreground text-xs sm:text-sm whitespace-nowrap">
                        {currencySymbol}{(position.pricePerShare || position.currentPrice).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      <span className="font-medium text-muted-foreground text-xs sm:text-sm whitespace-nowrap">
                        {currencySymbol}{(position.pricePerShare || position.currentPrice).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      <span className="font-medium text-muted-foreground text-xs sm:text-sm whitespace-nowrap">
                        {currencySymbol}{(position.pricePerShare || position.currentPrice).toFixed(2)}
                      </span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isGain ? (
                          <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4 text-red-600" />
                        )}
                        <span className={cn(
                          "font-semibold text-xs sm:text-sm whitespace-nowrap",
                          isGain ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                        )}>
                          {isGain ? '+' : ''}{gainPctDisplay.toFixed(2)}%
                        </span>
                      </div>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      <span className="font-bold text-foreground text-xs sm:text-sm whitespace-nowrap">
                        {position.totalValueCHF?.toLocaleString()} CHF
                      </span>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      {position.profitTargets && position.profitTargets.length > 0 && (() => {
                        const nextTarget = position.profitTargets.find(t => position.currentPrice < t.targetPrice);
                        return nextTarget ? (
                          <div>
                            <div className="font-bold text-green-600 text-sm whitespace-nowrap">
                              {currencySymbol}{nextTarget.targetPrice.toFixed(2)}
                            </div>
                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                              {nextTarget.ratio} (+{nextTarget.targetGainPct}%)
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-green-600 whitespace-nowrap">Target reached ✓</span>
                        );
                      })()}
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      {position.stopLoss && position.stopLoss[0] && (
                        <div>
                          <div className="font-bold text-red-600 text-sm whitespace-nowrap">
                            {currencySymbol}{position.stopLoss[0].price.toFixed(2)}
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">
                            {position.stopLoss[0].description.split(' ')[0]}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      {position.recommendedSellQty > 0 ? (
                        <div>
                          <div className="font-bold text-foreground text-sm sm:text-base whitespace-nowrap">{position.recommendedSellQty}</div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">({position.recommendedSellPct}%)</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">-</span>
                      )}
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      {position.capitalRecovered && position.capitalRecovered > 0 ? (
                        <div>
                          <div className="font-bold text-green-600 dark:text-green-400 text-sm sm:text-base whitespace-nowrap">
                            {currencySymbol}{(position.recommendedSellQty * position.currentPrice).toLocaleString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">-</span>
                      )}
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      {recoveryPercentage && Number(recoveryPercentage) > 0 ? (
                        <div className={cn(
                          "inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap",
                          alertInfo.hasAlert && "bg-primary/10 text-primary"
                        )}>
                          {recoveryPercentage}%
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">0%</span>
                      )}
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4 text-right">
                      <div className={cn(
                        "inline-flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full font-bold text-xs sm:text-sm whitespace-nowrap",
                        position.gainPct * 100 >= 45 && "animate-pulse",
                        position.gainPct * 100 >= 45 ? "bg-red-500 text-white" :
                        position.gainPct * 100 >= 30 ? "bg-orange-500 text-white" :
                        position.gainPct * 100 >= 15 ? "bg-yellow-500 text-white" :
                        "bg-gray-400 text-white"
                      )}>
                        {(position.gainPct * 100).toFixed(2)}%
                      </div>
                    </td>
                    <td className="py-2 sm:py-4 px-2 sm:px-4">
                      <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <div className="space-y-3">
            <div className="text-sm font-semibold text-foreground">Alert Legend:</div>
            <div className="flex items-center gap-6 text-sm flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span className="text-xs text-muted-foreground">≥45% Gain - Take Profit Now</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span className="text-xs text-muted-foreground">≥30% Gain - Profit Zone</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                <span className="text-xs text-muted-foreground">≥15% Gain - Profit Alert</span>
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              <strong>Profit Targets:</strong> Based on reward-to-risk ratios (2:1, 3:1, 4:1, 6:1)<br/>
              <strong>Stop Loss:</strong> Standard 5% below entry. Consider 10% for wider tolerance.
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
